"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const types_1 = require("./types");
const CookiesConsentScripts_json_1 = __importDefault(require("./CookiesConsentScripts.json"));
class CookiesConsentManager {
    /**
     * Construct a cookies consent manager object by decoding a consent string to work out which categories user has given consent for.
     * If using OneTrust as the cookie consent management interface, this consent string can be found by reading the OptanonConsent cookie.
     * @param consentString the string containing a user's consent information for different cookie categories.
     *    Example: landingPath=xxx&datestamp=Tue+Jun+18+2019+13%3A09%3A00+GMT%2B0100+(British+Summer+Time)&version=4.9.0&EU=true&groups=0_81893%3A1%2C111%3A0%2C1%3A1%2C0_81878%3A1%2C112%3A1%2C104%3A0%2C0_81874%3A1%2C0_81876%3A0%2C3%3A0%2C0_81875%3A1%2C103%3A0%2C106%3A0%2C4%3A0%2C0_81403%3A1%2C101%3A0&AwaitingReconsent=false
     * @param consentMode the consent mode that this manager should operate with. See ConsentMode for more details.
     */
    constructor(consentString = '', consentMode = types_1.ConsentMode.DEFAULT) {
        this.allowedCookiesCategories = new Set();
        this.consentMode = types_1.ConsentMode.DEFAULT;
        this.consentMode = consentMode;
        if (consentString) {
            const consentParams = this.getQueryStringParams(consentString);
            const rawCategoriesPermission = consentParams.groups || ''; // e.g. "1:1,2:0" meaning category 1 is allowed while 2 isn't.
            rawCategoriesPermission.split(',').forEach((categoryPermission) => {
                const [category, permission] = categoryPermission.split(':');
                if (this.isPermissionAllowed(permission)) {
                    this.allowedCookiesCategories.add(category);
                }
            });
        }
    }
    /**
     * Check if a permission string signals whether a cookie is allowed or not.
     * This string is often found in the consent string and '1' is allowed while '0' is not.
     * @param permission the permission string to check.
     */
    isPermissionAllowed(permission) {
        return Boolean(permission) && permission === '1';
    }
    /**
     * Get params from a query string
     * Source: https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
     */
    getQueryStringParams(query) {
        // eslint-disable-next-line no-nested-ternary
        const sanitised = query ? (/^[?#]/.test(query) ? query.slice(1) : query) : '';
        return sanitised.split('&').reduce((params, param) => {
            const [key, value] = param.split('=');
            // eslint-disable-next-line no-param-reassign
            params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
            return params;
        }, {});
    }
    /**
     * Returns whether a cookie is allowed to be set based on user's given consent.
     * @param cookieName the name of the cookie to check
     */
    isCookieAllowed(cookieName) {
        if (this.consentMode === types_1.ConsentMode.NOOP) {
            return true;
        }
        const cookieCategory = CookiesConsentManager.getCookieCategory(cookieName);
        if (this.consentMode === types_1.ConsentMode.STRICT) {
            return cookieCategory === types_1.CookiesCategory.StrictlyNecessary;
        }
        return (cookieCategory === types_1.CookiesCategory.StrictlyNecessary ||
            this.allowedCookiesCategories.has(cookieCategory));
    }
    /**
     * Returns category for a given cookie
     * @param cookieName the name of the cookie to retrieve category for
     */
    static getCookieCategory(cookieName) {
        switch (cookieName) {
            case 'basket':
            case 'browse_data':
            case 'consumer_auth_token':
            case 'locale':
            case 'roo_guid':
            case 'roo_session_guid':
                return types_1.CookiesCategory.StrictlyNecessary;
            case 'has_dismissed_mobile_banner':
            case 'seen_cookie_message':
            case 'lastViewedPlusPopup':
            case 'cwa_last_home_feed':
                return types_1.CookiesCategory.Functional;
            default:
                return types_1.CookiesCategory.Unrecognized;
        }
    }
    /**
     * Returns the cookies consent script as provided by vendor (OneTrust) for a given tld and locale
     * @param tld the tld to get consent script for, e.g. 'uk', 'fr'
     * @param locale the locale to get consent script for, e.g. 'en', 'de'
     * @param consentMode the consent mode for the consent script, i.e. if in NOOP or STRICT mode, we don't need a consent script
     */
    static getCookiesConsentScript(tld, locale, consentMode = types_1.ConsentMode.DEFAULT) {
        if (consentMode !== types_1.ConsentMode.DEFAULT) {
            return null;
        }
        const tldScripts = CookiesConsentScripts_json_1.default[tld] || {};
        // TODO: Can we do something to track if people are trying to access scripts that don't exist?
        return tldScripts[locale];
    }
}
exports.default = CookiesConsentManager;
